import { create } from 'zustand';
import type { ChatMessage, ChatSession, ChatMessageArtifact, ToolCallInfo } from '@/types';
import { generateId } from '@/lib/utils';

interface ChatState {
  sessions: ChatSession[];
  activeSessionId: string | null;
  isStreaming: boolean;
  streamingContent: string;
  thinkingContent: string;  // Extended thinking / reasoning content
}

interface ChatActions {
  createSession: (projectId?: string) => ChatSession;
  setActiveSession: (sessionId: string | null) => void;
  addMessage: (sessionId: string, message: Omit<ChatMessage, 'id' | 'createdAt'>) => string;
  updateMessage: (sessionId: string, messageId: string, content: string, toolCalls?: ToolCallInfo[], artifacts?: ChatMessageArtifact[]) => void;
  deleteMessage: (sessionId: string, messageId: string) => void;
  clearSession: (sessionId: string) => void;
  deleteSession: (sessionId: string) => void;
  setStreaming: (isStreaming: boolean) => void;
  appendStreamingContent: (content: string) => void;
  clearStreamingContent: () => void;
  appendThinkingContent: (content: string) => void;
  clearThinkingContent: () => void;
  getSessionMessages: (sessionId: string) => ChatMessage[];
  // New: Load sessions from backend
  loadSession: (session: ChatSession) => void;
  loadSessions: (sessions: ChatSession[]) => void;
  hasSession: (sessionId: string) => boolean;
  // Clear sessions for a specific project or all sessions
  clearProjectSessions: (projectId?: string) => void;
}

type ChatStore = ChatState & ChatActions;

export const useChatStore = create<ChatStore>((set, get) => ({
  sessions: [],
  activeSessionId: null,
  isStreaming: false,
  streamingContent: '',
  thinkingContent: '',

  createSession: (projectId) => {
    const session: ChatSession = {
      id: generateId(),
      projectId,
      title: 'New Chat',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    set((state) => ({
      sessions: [...state.sessions, session],
      activeSessionId: session.id,
    }));

    return session;
  },

  setActiveSession: (sessionId) =>
    set({
      activeSessionId: sessionId,
    }),

  addMessage: (sessionId, message) => {
    const newMessage: ChatMessage = {
      ...message,
      id: generateId(),
      createdAt: new Date().toISOString(),
    };

    set((state) => ({
      sessions: state.sessions.map((session) =>
        session.id === sessionId
          ? {
            ...session,
            messages: [...session.messages, newMessage],
            updatedAt: new Date().toISOString(),
            title:
              session.messages.length === 0 && message.role === 'user'
                ? message.content.slice(0, 50)
                : session.title,
          }
          : session
      ),
    }));

    return newMessage.id;
  },

  updateMessage: (sessionId, messageId, content, toolCalls, artifacts) =>
    set((state) => ({
      sessions: state.sessions.map((session) =>
        session.id === sessionId
          ? {
            ...session,
            messages: session.messages.map((msg) =>
              msg.id === messageId
                ? {
                  ...msg,
                  content,
                  ...(toolCalls !== undefined && { toolCalls }),
                  ...(artifacts !== undefined && { artifacts }),
                }
                : msg
            ),
            updatedAt: new Date().toISOString(),
          }
          : session
      ),
    })),

  deleteMessage: (sessionId, messageId) =>
    set((state) => ({
      sessions: state.sessions.map((session) =>
        session.id === sessionId
          ? {
            ...session,
            messages: session.messages.filter((msg) => msg.id !== messageId),
            updatedAt: new Date().toISOString(),
          }
          : session
      ),
    })),

  clearSession: (sessionId) =>
    set((state) => ({
      sessions: state.sessions.map((session) =>
        session.id === sessionId
          ? {
            ...session,
            messages: [],
            updatedAt: new Date().toISOString(),
          }
          : session
      ),
    })),

  deleteSession: (sessionId) =>
    set((state) => ({
      sessions: state.sessions.filter((s) => s.id !== sessionId),
      activeSessionId:
        state.activeSessionId === sessionId ? null : state.activeSessionId,
    })),

  setStreaming: (isStreaming) =>
    set({
      isStreaming,
    }),

  appendStreamingContent: (content) =>
    set((state) => ({
      streamingContent: state.streamingContent + content,
    })),

  clearStreamingContent: () =>
    set({
      streamingContent: '',
    }),

  appendThinkingContent: (content) =>
    set((state) => ({
      thinkingContent: state.thinkingContent + content,
    })),

  clearThinkingContent: () =>
    set({
      thinkingContent: '',
    }),

  getSessionMessages: (sessionId) => {
    const session = get().sessions.find((s) => s.id === sessionId);
    return session?.messages || [];
  },

  // Load a single session from backend (with messages)
  loadSession: (session) => {
    set((state) => {
      const existingIndex = state.sessions.findIndex((s) => s.id === session.id);
      if (existingIndex >= 0) {
        // Update existing session
        const newSessions = [...state.sessions];
        newSessions[existingIndex] = session;
        return { sessions: newSessions };
      } else {
        // Add new session
        return { sessions: [...state.sessions, session] };
      }
    });
  },

  // Load multiple sessions (without messages, for sidebar list)
  loadSessions: (sessions) => {
    set((state) => {
      const existingIds = new Set(state.sessions.map((s) => s.id));
      const newSessions = sessions.filter((s) => !existingIds.has(s.id));
      return { sessions: [...state.sessions, ...newSessions] };
    });
  },

  // Check if session exists in store
  hasSession: (sessionId) => {
    return get().sessions.some((s) => s.id === sessionId);
  },

  // Clear sessions for a specific project (or global sessions if projectId is undefined)
  clearProjectSessions: (projectId) => {
    set((state) => ({
      sessions: state.sessions.filter((s) =>
        projectId ? s.projectId !== projectId : s.projectId !== undefined
      ),
      activeSessionId:
        state.activeSessionId &&
          state.sessions.find((s) => s.id === state.activeSessionId)?.projectId === projectId
          ? null
          : state.activeSessionId,
    }));
  },
}));
